import { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  type RuleActivityLog,
  type RuleRunLogDetail,
  type RuleMetricsDto,
} from "../../api/RuleApi";
import RuleAction from "../../actions/RuleAction";
import { format, formatDistanceToNow } from "date-fns";
import DropdownLayout from "../../components/DropdownLayout";
import {
  ChevronDownIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  ChevronUpIcon,
  CloudArrowDownIcon,
  CurrencyDollarIcon,
} from "@heroicons/react/24/outline";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { CheckCircleIcon, XCircleIcon } from "@heroicons/react/24/solid";

type RuleLogListProps = {
  showLog: { ruleId: string };
  projectId: string;
  sidebar?: boolean;
};

export const RuleLogList = ({ showLog, projectId, sidebar = false }: RuleLogListProps) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const ruleLogs = useSelector((state: any) => state.ruleReducer.get("ruleLogs"));
  const ruleLogsTotalPages = useSelector((state: any) =>
    state.ruleReducer.get("ruleLogsTotalPages")
  );
  const ruleLogsTotalElements = useSelector((state: any) =>
    state.ruleReducer.get("ruleLogsTotalElements")
  );
  const ruleLogDetail = useSelector((state: any) => state.ruleReducer.get("ruleLogDetail"));
  const ruleMetrics = useSelector((state: any) => state.ruleReducer.get("ruleMetrics"));
  const loadingLogs = useSelector((state: any) => state.ruleReducer.get("pendingRuleLogs"));
  const loadingDetail = useSelector((state: any) => state.ruleReducer.get("pendingRuleLogDetail"));
  const loadingMetrics = useSelector((state: any) => state.ruleReducer.get("pendingRuleMetrics"));

  const [selectedStatus, setSelectedStatus] = useState("ALL");
  const [selectedLogId, setSelectedLogId] = useState<string | null>(null);
  const [page, setPage] = useState(0);
  const [detailExpanded, setDetailExpanded] = useState(true);
  const runDetailPanelRef = useRef<HTMLDivElement>(null);

  const pageSize = sidebar ? 20 : 50;
  const logs: RuleActivityLog[] = ruleLogs ? ruleLogs.toJS() : [];
  const logDetail: RuleRunLogDetail | null = ruleLogDetail ? ruleLogDetail.toJS() : null;
  const totalPages = ruleLogsTotalPages ?? 0;
  const totalElements = ruleLogsTotalElements ?? 0;
  const metrics: RuleMetricsDto | null = ruleMetrics ? ruleMetrics.toJS() : null;

  useEffect(() => {
    if (showLog.ruleId === "") {
      dispatch(RuleAction.clearRuleLogs());
      return;
    }
    dispatch(RuleAction.setRuleLogDetail(null));
  }, [showLog.ruleId]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (showLog.ruleId !== "") setPage(0);
  }, [showLog.ruleId]);

  useEffect(() => {
    if (showLog.ruleId === "") return;
    const currentPage = sidebar ? page : 0;
    dispatch(RuleAction.getRuleLogs(showLog.ruleId, currentPage, pageSize));
  }, [showLog.ruleId, sidebar, page, pageSize]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (showLog.ruleId !== "" && sidebar) {
      dispatch(RuleAction.getRuleMetrics(showLog.ruleId));
    }
  }, [showLog.ruleId, sidebar]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!selectedLogId || !showLog.ruleId) {
      dispatch(RuleAction.setRuleLogDetail(null));
      return;
    }
    dispatch(RuleAction.getRuleLogDetail(showLog.ruleId, selectedLogId));
  }, [selectedLogId, showLog.ruleId]); // eslint-disable-line react-hooks/exhaustive-deps

  const logStatus = ["ALL", "ENQUEUED", "PROCESSING", "SUCCEEDED", "FAILED"];

  /** Human-readable datetime: relative for recent (e.g. "2 hours ago"), absolute otherwise. */
  const formatRunDateTime = (createdAt: number | string | null | undefined) => {
    if (createdAt == null || createdAt === "") return "—";
    const ts = typeof createdAt === "number" ? createdAt : Number(createdAt);
    if (Number.isNaN(ts)) return "—";
    const date = new Date(ts);
    if (Number.isNaN(date.getTime())) return "—";
    const now = Date.now();
    const sevenDaysMs = 7 * 24 * 60 * 60 * 1000;
    if (now - date.getTime() < sevenDaysMs) {
      return formatDistanceToNow(date, { addSuffix: true });
    }
    return format(date, "PPp");
  };

  /** UI color for run status text only. */
  const getRunStatusBadgeClass = (state: string) => {
    switch (state) {
      case "SUCCEEDED":
        return "text-green-700";
      case "FAILED":
        return "text-red-700";
      case "PROCESSING":
        return "text-amber-700";
      case "ENQUEUED":
        return "text-slate-600";
      default:
        return "text-gray-600";
    }
  };

  /** When preventNavigation is true (e.g. in run list card), clicking opens run detail instead of going to item. */
  const renderItemInfo = (log: RuleActivityLog, preventNavigation = false) => {
    if (log.itemType === "CSV_CONTACT") {
      return (
        <div className={"flex space-x-2 w-full items-center hover:text-blue-600"}>
          <CloudArrowDownIcon className={"w-6"} /> <span>CSV</span>
        </div>
      );
    }
    if (log.itemType === "SALE") {
      const content = (
        <>
          <CurrencyDollarIcon className={"w-6"} /> {log.itemName}
        </>
      );
      if (preventNavigation) {
        return (
          <span className="text-ellipsis flex space-x-1 text-blue-600 min-w-0">{content}</span>
        );
      }
      return (
        <Link
          to={`/workspaces/${projectId}/items/${log.itemId}?type=${log.itemType}`}
          className={"text-ellipsis flex space-x-1 text-blue-600"}
          onClick={(e) => e.stopPropagation()}
        >
          {content}
        </Link>
      );
    }
    return <div>{log.itemName}</div>;
  };

  const handleLogRowClick = (log: RuleActivityLog) => {
    if (log.id) {
      setSelectedLogId(log.id);
      setDetailExpanded(true);
    }
  };

  /** Human-readable label for AI_FIELD step context.aiCost.aiFieldAction */
  const aiFieldActionLabel = (action: string | undefined): string => {
    if (!action) return "AI Field";
    const labels: Record<string, string> = {
      researchRecord: "Research record",
      summarizeRecord: "Summarize record",
      classifyRecord: "Classify record",
      classifyText: "Classify text",
      promptCompletion: "Prompt completion",
    };
    return labels[action] ?? action;
  };

  const renderTableLog = () => {
    const list = logs ?? [];
    const filtered =
      selectedStatus == "ALL" ? list : list.filter((item) => item.state == selectedStatus);
    if (filtered.length == 0) {
      return null;
    }
    return (
      <table className={"p-4 w-full"}>
        <thead>
          <tr>
            <th className={"w-48"}>{t("Status")}</th>
            <th className={"w-fit"}>{t("Date & time")}</th>
            <th></th>
          </tr>
        </thead>
        <tbody>
          {filtered.map((log, index) => (
            <tr
              key={log.id ?? `log_${index}`}
              className={`hover:bg-gray-100 h-16 py-4 rounded-sm ${log.id ? "cursor-pointer" : ""} ${selectedLogId === log.id ? "bg-blue-50" : ""}`}
              onClick={() => log.id && handleLogRowClick(log)}
            >
              <td align={"center"}>
                <span className={`font-medium ${getRunStatusBadgeClass(log.state)}`}>
                  {t(log.state)}
                </span>
              </td>
              <td align={"center"}>{formatRunDateTime(log.createdAt)}</td>
              <td align={"center"} className={"text-sm"}>
                {renderItemInfo(log, true)}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    );
  };

  /** Full run detail: summary, metrics, message, steps list (for expandable panel). */
  const renderRunDetailContent = (_expandStepContext = true) => {
    if (loadingDetail) return <div className="text-gray-500 py-4 text-sm">{t("Loading…")}</div>;
    if (!logDetail) return null;
    const steps = logDetail.steps ?? [];
    const isFailed = logDetail.jobState === "FAILED";
    const isSuccess = logDetail.jobState === "SUCCEEDED";
    return (
      <div className="space-y-3 p-4">
        {/* ── Header: Item + Status ── */}
        <div className="flex items-start justify-between gap-2">
          <div className="min-w-0">
            <div className="font-semibold text-gray-900 text-sm truncate">
              {logDetail.itemType === "SALE" ||
              logDetail.itemType === "CONTACT" ||
              logDetail.itemType === "COMPANY" ||
              logDetail.itemType === "PRODUCT" ? (
                <Link
                  to={`/workspaces/${projectId}/items/${logDetail.itemId}?type=${logDetail.itemType}`}
                  className="text-blue-600 hover:underline"
                >
                  {logDetail.itemName}
                </Link>
              ) : (
                logDetail.itemName
              )}
            </div>
            <div className="flex items-center gap-2 mt-1 text-xs text-gray-500">
              <span className="uppercase font-medium">{logDetail.itemType}</span>
              <span>·</span>
              <span>{logDetail.createdAt ? formatRunDateTime(logDetail.createdAt) : "—"}</span>
            </div>
          </div>
          <span
            className={`shrink-0 inline-flex items-center gap-1 px-2 py-1 rounded-md text-xs font-medium ${
              isFailed
                ? "bg-red-100 text-red-700"
                : isSuccess
                  ? "bg-green-100 text-green-700"
                  : "bg-amber-100 text-amber-700"
            }`}
          >
            {isSuccess && <CheckCircleIcon className="w-3.5 h-3.5" />}
            {isFailed && <XCircleIcon className="w-3.5 h-3.5" />}
            {t(logDetail.jobState)}
          </span>
        </div>

        {/* ── Metrics row ── */}
        <div className="grid grid-cols-4 gap-1.5">
          <div className="rounded-md bg-gray-50 border border-gray-100 px-2.5 py-1.5 text-center">
            <div className="text-sm font-bold text-gray-800">
              {logDetail.metrics?.stepCount ?? 0}
            </div>
            <div className="text-[10px] text-gray-500">{t("Steps")}</div>
          </div>
          <div className="rounded-md bg-gray-50 border border-gray-100 px-2.5 py-1.5 text-center">
            <div className="text-sm font-bold text-green-700">
              {logDetail.metrics?.successCount ?? 0}
            </div>
            <div className="text-[10px] text-gray-500">{t("Success")}</div>
          </div>
          <div className="rounded-md bg-gray-50 border border-gray-100 px-2.5 py-1.5 text-center">
            <div className="text-sm font-bold text-red-700">
              {logDetail.metrics?.failedCount ?? 0}
            </div>
            <div className="text-[10px] text-gray-500">{t("Failed")}</div>
          </div>
          <div className="rounded-md bg-gray-50 border border-gray-100 px-2.5 py-1.5 text-center">
            <div className="text-sm font-bold text-gray-800">
              {logDetail.metrics?.totalDurationMs ?? 0}ms
            </div>
            <div className="text-[10px] text-gray-500">{t("Duration")}</div>
          </div>
        </div>

        {/* ── Error message ── */}
        {logDetail.jobMessage && (
          <details className="group" open>
            <summary className="flex items-center gap-1.5 text-xs font-medium text-red-600 cursor-pointer select-none">
              <XCircleIcon className="w-3.5 h-3.5" />
              {t("Error")}
              <ChevronDownIcon className="w-3 h-3 group-open:rotate-180 transition-transform" />
            </summary>
            <div className="mt-1.5 text-xs text-red-700 bg-red-50 border border-red-100 rounded-md p-2.5 max-h-32 overflow-y-auto font-mono leading-relaxed break-all">
              {logDetail.jobMessage}
            </div>
          </details>
        )}

        {/* ── Steps ── */}
        {steps.length > 0 && (
          <div>
            <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">
              {t("Steps")} ({steps.length})
            </h4>
            <div className="space-y-0">
              {steps.map((step, i) => {
                const stepCtx = step.context as Record<string, unknown> | undefined;
                const aiCost = stepCtx?.aiCost as Record<string, unknown> | undefined;
                const stepAction = aiCost?.aiFieldAction as string | undefined;
                const isAiField = step.operator === "AI_FIELD";
                const stepSuccess = step.status === "SUCCESS";
                const stepFailed = step.status === "FAILED";
                return (
                  <div key={`step_${i}`} className="flex gap-3">
                    {/* Timeline dot + line */}
                    <div className="flex flex-col items-center">
                      <div
                        className={`w-2.5 h-2.5 rounded-full mt-1.5 shrink-0 ${
                          stepFailed ? "bg-red-500" : stepSuccess ? "bg-green-500" : "bg-gray-300"
                        }`}
                      />
                      {i < steps.length - 1 && <div className="w-px flex-1 bg-gray-200 my-1" />}
                    </div>
                    {/* Step content */}
                    <div className="pb-3 min-w-0 flex-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-xs font-mono text-gray-400">
                          #{(step.actionIndex ?? i) + 1}
                        </span>
                        <span className="text-sm font-medium text-gray-900">
                          {isAiField ? aiFieldActionLabel(stepAction) : step.operator}
                        </span>
                        <span className="text-xs text-gray-400">
                          {step.inputValue?.itemType ?? ""}
                        </span>
                        <span className="text-xs text-gray-500 ml-auto">
                          {step.executionTime?.durationMs ?? 0}ms
                        </span>
                      </div>
                      {step.statusMessage && (
                        <div className="text-xs text-red-600 mt-1 bg-red-50 rounded px-2 py-1 break-all font-mono max-h-20 overflow-y-auto">
                          {step.statusMessage}
                        </div>
                      )}
                      {step.context && Object.keys(step.context).length > 0 && (
                        <details className="mt-1.5">
                          <summary className="text-[11px] text-gray-400 cursor-pointer hover:text-gray-600">
                            {t("Context")}
                          </summary>
                          <pre className="text-[11px] bg-gray-50 border border-gray-100 p-2 rounded mt-1 overflow-auto max-h-32 text-gray-600">
                            {JSON.stringify(step.context, null, 2)}
                          </pre>
                        </details>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}
        {steps.length === 0 && (
          <p className="text-xs text-gray-400 italic">{t("No steps recorded.")}</p>
        )}
      </div>
    );
  };

  const renderLogs = () => {
    const statusFilterBtn = (
      <div className={"cursor-pointer underline uppercase pb-4 w-60"}>
        {t("Status")}: {t(selectedStatus)}
      </div>
    );

    return (
      <div className={"text-lg p-4"}>
        <div className={"float-left"}>
          <DropdownLayout activator={statusFilterBtn}>
            <div className={"w-full min-h-96"}>
              <ul className={"w-full"}>
                {logStatus.map((item) => (
                  <li
                    className={"px-3 py-2 hover:bg-blue-400 hover:text-white cursor-pointer w-full"}
                    key={`status_${item}`}
                    onClick={() => {
                      setSelectedStatus(item);
                    }}
                  >
                    {t(item)}
                  </li>
                ))}
              </ul>
            </div>
          </DropdownLayout>
        </div>
        <div className={"float-right"}>{t("Activity log (only 50 latest items)")}</div>

        {renderTableLog()}
      </div>
    );
  };

  if (showLog.ruleId === "") {
    return null;
  }

  if (sidebar) {
    const list = logs ?? [];
    const filtered =
      selectedStatus === "ALL" ? list : list.filter((item) => item.state === selectedStatus);
    return (
      <div className="flex flex-col h-full overflow-hidden flex-1 min-w-0">
        <div className="shrink-0 px-4 py-3 border-b border-gray-200 bg-white">
          <h3 className="font-semibold text-gray-800 text-[15px]">{t("Run history")}</h3>
          {/* Status filter tabs */}
          <div className="flex items-center gap-1 mt-2.5 flex-wrap">
            {logStatus.map((item) => (
              <button
                key={`status_${item}`}
                type="button"
                className={`px-3 py-1.5 rounded-md text-xs font-medium transition-colors ${
                  selectedStatus === item
                    ? "bg-gray-900 text-white"
                    : "bg-gray-100 text-gray-600 hover:bg-gray-200"
                }`}
                onClick={() => setSelectedStatus(item)}
              >
                {t(item)}
              </button>
            ))}
          </div>
        </div>
        <div
          className={`divide-y divide-gray-200 overflow-hidden rounded-lg bg-gray-200 shadow-sm sm:divide-y-0 dark:divide-white/10 dark:bg-gray-900 dark:shadow-none dark:outline dark:-outline-offset-1 dark:outline-white/20 ${selectedLogId ? "sm:grid sm:grid-cols-2" : ""}`}
        >
          {/* Column 2: run list + pagination + overview */}
          <div
            className={`shrink-0 flex flex-col bg-white overflow-hidden ${selectedLogId ? "border-r border-gray-200" : ""}`}
          >
            <div className="flex-1 min-h-0 overflow-y-auto">
              <div className="divide-y divide-gray-100">
                {loadingLogs ? (
                  <p className="text-sm text-gray-500 p-4">{t("Loading…")}</p>
                ) : filtered.length === 0 ? (
                  <p className="text-sm text-gray-500 p-4">{t("No runs yet.")}</p>
                ) : (
                  filtered.map((log, index) => (
                    <button
                      key={log.id ?? `log_${index}`}
                      type="button"
                      className={`w-full text-left px-4 py-2.5 flex items-center gap-3 transition-colors ${
                        selectedLogId === log.id
                          ? "bg-blue-50 border-l-2 border-l-blue-500"
                          : "hover:bg-gray-50 border-l-2 border-l-transparent"
                      } ${log.id ? "cursor-pointer" : ""}`}
                      onClick={() => log.id && handleLogRowClick(log)}
                    >
                      {/* Status icon */}
                      {log.state === "SUCCEEDED" ? (
                        <CheckCircleIcon className="w-5 h-5 text-green-500 shrink-0" />
                      ) : log.state === "FAILED" ? (
                        <XCircleIcon className="w-5 h-5 text-red-500 shrink-0" />
                      ) : log.state === "PROCESSING" ? (
                        <div className="w-5 h-5 shrink-0 rounded-full border-2 border-blue-400 border-t-transparent animate-spin" />
                      ) : (
                        <div className="w-5 h-5 shrink-0 rounded-full border-2 border-gray-300" />
                      )}
                      {/* Run info */}
                      <span className="text-sm font-medium text-gray-900 whitespace-nowrap">
                        {t("Run #{{n}}", { n: totalElements - page * pageSize - index })}
                      </span>
                      {/* Spacer */}
                      <span className="flex-1" />
                      {/* Timestamp */}
                      <span className="text-xs text-gray-400 whitespace-nowrap">
                        {formatRunDateTime(log.createdAt)}
                      </span>
                    </button>
                  ))
                )}
              </div>
              {totalPages > 0 && (
                <div className="flex items-center justify-between gap-2 px-3 py-2 border-t border-gray-200 bg-gray-50">
                  <span className="text-xs text-gray-600">
                    {t("Page")} {page + 1} {t("of")} {totalPages}
                  </span>
                  <div className="flex gap-1">
                    <button
                      type="button"
                      disabled={page <= 0}
                      onClick={() => setPage((p) => Math.max(0, p - 1))}
                      className="rounded border border-gray-300 bg-white p-1.5 text-gray-700 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-100"
                      aria-label={t("Previous page")}
                    >
                      <ChevronLeftIcon className="w-4 h-4" />
                    </button>
                    <button
                      type="button"
                      disabled={page >= totalPages - 1}
                      onClick={() => setPage((p) => Math.min(totalPages - 1, p + 1))}
                      className="rounded border border-gray-300 bg-white p-1.5 text-gray-700 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-100"
                      aria-label={t("Next page")}
                    >
                      <ChevronRightIcon className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              )}
            </div>
            {/* Overview – single row metrics */}
            {!loadingMetrics && metrics && (
              <div className="shrink-0 border-t border-gray-200 bg-white px-4 py-3">
                <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">
                  {t("Overview")}
                </h4>
                <div className="grid grid-cols-4 gap-1.5">
                  <div className="rounded-lg border border-gray-100 bg-green-50/50 px-2.5 py-2 text-center">
                    <div className="text-lg font-bold text-green-700">{metrics.completedCount}</div>
                    <div className="text-[10px] text-gray-500">{t("Completed")}</div>
                  </div>
                  <div className="rounded-lg border border-gray-100 bg-red-50/50 px-2.5 py-2 text-center">
                    <div className="text-lg font-bold text-red-700">{metrics.failedCount}</div>
                    <div className="text-[10px] text-gray-500">{t("Failed")}</div>
                  </div>
                  <div className="rounded-lg border border-gray-100 bg-gray-50 px-2.5 py-2 text-center">
                    <div className="text-lg font-bold text-amber-600">
                      {metrics.inProgressCount}
                    </div>
                    <div className="text-[10px] text-gray-500">{t("In progress")}</div>
                  </div>
                  <div className="rounded-lg border border-gray-100 bg-gray-50 px-2.5 py-2 text-center">
                    <div className="text-lg font-bold text-gray-700">
                      {metrics.avgDurationMs != null ? `${metrics.avgDurationMs}ms` : "—"}
                    </div>
                    <div className="text-[10px] text-gray-500">{t("Avg. runtime")}</div>
                  </div>
                </div>
                {metrics.totalAiCostUsd != null && metrics.totalAiCostUsd > 0 && (
                  <div className="mt-1.5 flex items-center justify-between text-xs text-gray-500 px-1">
                    <span>AI: ${metrics.totalAiCostUsd.toFixed(4)}</span>
                    {metrics.totalTokenUsage != null && (
                      <span>
                        {metrics.totalTokenUsage.toLocaleString()} {t("tokens")}
                      </span>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>
          {/* Column 3: run detail — only when a run is selected */}
          {selectedLogId && (
            <div className="shrink-0 flex flex-col bg-gray-50 overflow-hidden border-l border-gray-200">
              <div className="shrink-0 px-4 py-3 border-b border-gray-200 bg-white">
                <h4 className="font-semibold text-gray-800 text-sm min-w-0 truncate">
                  {logDetail
                    ? `${logDetail.itemName} · ${t(logDetail.jobState)}`
                    : t("Rule run details")}
                </h4>
              </div>
              <div className="flex-1 min-h-0 overflow-y-auto">{renderRunDetailContent(true)}</div>
            </div>
          )}
        </div>
      </div>
    );
  }

  return (
    <>
      {renderLogs()}
      {selectedLogId && (
        <div
          ref={runDetailPanelRef}
          className="mt-4 border z-50 border-gray-200 rounded-lg bg-white overflow-hidden"
        >
          <button
            type="button"
            className="w-full flex items-center justify-between gap-2 p-4 text-left hover:bg-gray-50 focus:outline-none focus:ring-1 focus:ring-gray-200"
            onClick={() => setDetailExpanded((e) => !e)}
            aria-expanded={detailExpanded}
          >
            <span className="font-semibold text-gray-700">
              {logDetail
                ? `${logDetail.itemName} · ${t(logDetail.jobState)}`
                : t("Rule run details")}
            </span>
            {detailExpanded ? (
              <ChevronUpIcon className="w-5 h-5 text-gray-500 shrink-0" />
            ) : (
              <ChevronDownIcon className="w-5 h-5 text-gray-500 shrink-0" />
            )}
          </button>
          {detailExpanded && (
            <div className="border-t border-gray-200 overflow-y-auto max-h-[70vh] min-h-[120px]">
              {renderRunDetailContent(true)}
            </div>
          )}
        </div>
      )}
    </>
  );
};

export default RuleLogList;
